/**
 * Signing in with Keycloak: authorization code with PKCE against the realm's endpoints (built from
 * the issuer, no discovery request), tokens kept in the platform's storage, the access token
 * refreshed a minute before it expires. Who you are comes from the ID token's claims.
 */
import { logger } from '@core/log';
import { callbackUrl, openAuth, redirectUri } from '@platform/auth-browser';
import type { Storage } from '@platform/storage';

const log = logger('auth');

export interface Tokens {
  accessToken: string;
  /** When the access token expires, server-ish epoch milliseconds from the device clock. */
  expiresAt: number;
  refreshToken: string | null;
  idToken: string | null;
}

export interface Identity {
  /** Keycloak's subject: the player id everywhere. */
  id: string;
  /** The first name, or what the account offers instead. */
  name: string;
  email: string | null;
}

export interface AuthSettings {
  issuer: string;
  clientId: string;
}

export interface AuthDeps {
  storage: Storage;
  fetchApi?: typeof fetch;
  now?: () => number;
}

/** `out` carries `failed` when a sign-in attempt just went wrong, so the lobby can say so. */
export type AuthState =
  { kind: 'out'; failed?: boolean } | { kind: 'in'; identity: Identity } | { kind: 'busy' };

export class SessionExpired extends Error {
  constructor() {
    super('The session expired');
  }
}

const TOKENS_KEY = 'auth:tokens';
const FLOW_KEY = 'auth:flow';
const EXPIRY_MARGIN_MS = 60_000;

export const settingsFromEnv = (): AuthSettings => ({
  issuer: (
    import.meta.env.VITE_OIDC_ISSUER ?? 'https://test.auth.terrella.cloud/realms/terrella'
  ).replace(/\/$/, ''),
  clientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? 'terrella-app',
});

/** base64url of secure-random bytes: a code verifier (32 bytes) or a state (16 bytes). */
function randomString(bytes: number): string {
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return base64url(array);
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** The claims of a JWT, read without checking it: the server checks every token, the app only names you. */
export function claimsOf(jwt: string): Record<string, unknown> | null {
  const payload = jwt.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(
      payload
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(payload.length / 4) * 4, '='),
    );
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function identityOf(tokens: Tokens): Identity | null {
  const claims = (tokens.idToken && claimsOf(tokens.idToken)) ?? claimsOf(tokens.accessToken);
  if (!claims || typeof claims['sub'] !== 'string') return null;
  const text = (key: string): string | null =>
    typeof claims[key] === 'string' && claims[key] ? claims[key] : null;
  const name =
    text('given_name') ??
    text('name') ??
    text('preferred_username') ??
    text('email') ??
    claims['sub'];
  return {
    id: claims['sub'],
    name: name.trim().slice(0, 24) || 'Player',
    email: text('email'),
  };
}

function formBody(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

export interface Auth {
  state(): AuthState;
  subscribe(listener: (state: AuthState) => void): () => void;
  /** Restores the session from storage (refreshing the token) or finishes a web redirect. */
  start(): Promise<void>;
  /** Opens Keycloak's sign-in (or its registration form). Resolves when signed in, or when the person gave up. */
  login(flow?: 'login' | 'register'): Promise<void>;
  logout(): Promise<void>;
  /** A valid access token, refreshed when needed (or when `fresh` asks); null when signed out. */
  accessToken(fresh?: boolean): Promise<string | null>;
  identity(): Identity | null;
}

export function createAuth(settings: AuthSettings, deps: AuthDeps): Auth {
  const { storage } = deps;
  const fetchApi = deps.fetchApi ?? ((input, init) => fetch(input, init));
  const now = deps.now ?? (() => Date.now()); // eslint-disable-line no-restricted-globals, no-restricted-syntax -- token lifetimes are wall-clock
  const endpoint = (path: string): string => `${settings.issuer}/protocol/openid-connect/${path}`;
  const listeners = new Set<(state: AuthState) => void>();
  let tokens: Tokens | null = null;
  let current: AuthState = { kind: 'out' };
  let refreshing: Promise<Tokens> | null = null;

  const setState = (next: AuthState): void => {
    current = next;
    for (const listener of [...listeners]) listener(next);
  };

  const save = async (next: Tokens | null): Promise<void> => {
    tokens = next;
    if (next) await storage.set(TOKENS_KEY, JSON.stringify(next));
    else await storage.remove(TOKENS_KEY);
    const identity = next ? identityOf(next) : null;
    setState(identity ? { kind: 'in', identity } : { kind: 'out' });
  };

  async function requestTokens(params: Record<string, string>): Promise<Tokens> {
    const response = await fetchApi(endpoint('token'), {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: formBody({ client_id: settings.clientId, ...params }),
    });
    if (response.status === 400 || response.status === 401) throw new SessionExpired();
    if (!response.ok) throw new Error(`token endpoint answered ${response.status}`);
    const body = (await response.json()) as Record<string, unknown>;
    if (typeof body['access_token'] !== 'string') throw new Error('no access token');
    const expiresIn = typeof body['expires_in'] === 'number' ? body['expires_in'] : 300;
    return {
      accessToken: body['access_token'],
      expiresAt: now() + expiresIn * 1000,
      refreshToken:
        typeof body['refresh_token'] === 'string'
          ? body['refresh_token']
          : (tokens?.refreshToken ?? null),
      idToken: typeof body['id_token'] === 'string' ? body['id_token'] : (tokens?.idToken ?? null),
    };
  }

  async function refresh(): Promise<Tokens> {
    if (refreshing) return refreshing;
    const refreshToken = tokens?.refreshToken;
    if (!refreshToken) throw new SessionExpired();
    refreshing = requestTokens({ grant_type: 'refresh_token', refresh_token: refreshToken })
      .then(async (next) => {
        await save(next);
        return next;
      })
      .catch(async (error: unknown) => {
        if (error instanceof SessionExpired) await save(null);
        throw error;
      })
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  /** The web redirect back from Keycloak, or an appUrlOpen on the phone. */
  async function finish(url: string): Promise<void> {
    const params = new URL(url).searchParams;
    const flow = await storage.get(FLOW_KEY);
    await storage.remove(FLOW_KEY);
    const error = params.get('error');
    if (error) {
      log.warn('sign-in refused', error, params.get('error_description'));
      setState({ kind: 'out', failed: error !== 'access_denied' });
      return;
    }
    const code = params.get('code');
    const stored = flow ? (JSON.parse(flow) as { state: string; verifier: string }) : null;
    if (!code || params.get('state') !== stored?.state) {
      log.warn('sign-in came back without a matching state');
      setState({ kind: 'out', failed: true });
      return;
    }
    const next = await requestTokens({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri(),
      code_verifier: stored.verifier,
    });
    await save(next);
  }

  return {
    state: () => current,
    identity: () => (current.kind === 'in' ? current.identity : null),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async start() {
      const back = callbackUrl();
      if (back) {
        setState({ kind: 'busy' });
        try {
          await finish(back);
        } catch (error) {
          log.warn('sign-in failed', error);
          setState({ kind: 'out', failed: true });
        }
        return;
      }
      const stored = await storage.get(TOKENS_KEY);
      if (!stored) return;
      try {
        tokens = JSON.parse(stored) as Tokens;
      } catch {
        await storage.remove(TOKENS_KEY);
        return;
      }
      const identity = identityOf(tokens);
      if (!identity) {
        await save(null);
        return;
      }
      setState({ kind: 'in', identity });
      if (tokens.expiresAt - EXPIRY_MARGIN_MS <= now()) {
        try {
          await refresh();
        } catch (error) {
          if (!(error instanceof SessionExpired))
            log.warn('refresh failed, keeping the session for now', error);
        }
      }
    },
    async login(flow = 'login') {
      const verifier = randomString(32);
      const state = randomString(16);
      await storage.set(FLOW_KEY, JSON.stringify({ state, verifier }));
      const url = new URL(endpoint(flow === 'register' ? 'registrations' : 'auth'));
      url.search = formBody({
        client_id: settings.clientId,
        response_type: 'code',
        scope: 'openid profile email offline_access',
        redirect_uri: redirectUri(),
        state,
        code_challenge: await codeChallenge(verifier),
        code_challenge_method: 'S256',
      });
      setState({ kind: 'busy' });
      const back = await openAuth(url.toString());
      if (!back) {
        await storage.remove(FLOW_KEY);
        setState({ kind: 'out' });
        return;
      }
      try {
        await finish(back);
      } catch (error) {
        log.warn('sign-in failed', error);
        setState({ kind: 'out', failed: true });
      }
    },
    async logout() {
      const refreshToken = tokens?.refreshToken;
      await save(null);
      if (refreshToken) {
        try {
          await fetchApi(endpoint('logout'), {
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            body: formBody({ client_id: settings.clientId, refresh_token: refreshToken }),
          });
        } catch {
          /* the token dies on its own */
        }
      }
    },
    async accessToken(fresh = false) {
      if (!tokens) return null;
      if (!fresh && tokens.expiresAt - EXPIRY_MARGIN_MS > now()) return tokens.accessToken;
      try {
        return (await refresh()).accessToken;
      } catch (error) {
        if (error instanceof SessionExpired) return null;
        return tokens.accessToken; // offline: try with what we have
      }
    },
  };
}
